 
import React from 'react';
import { formatNumber } from '@cars4ever/public-ui';

import { FuelType, GearType, CommonStep, CommonAction, EnergyClass, BodyType, CarType, PaintType } from '@digital-dealer/enums';

const basicTexts = {
  common: {
    moneyUnits: {
      default: 'kr.',
      businessType: 'kr.',
    },
    fuelTypes: {
      [FuelType.Diesel]: 'Diesel',
      [FuelType.Petrol]: 'Benzin',
      [FuelType.Hybrid]: 'Plug-in hybrid',
      [FuelType.Electric]: 'El',
      [FuelType.MildHybrid]: 'Mild hybrid',
      [FuelType.EREV]: 'EREV',
    },
    gearTypes: {
      [GearType.Automatic]: 'Automatisk',
      [GearType.Manual]: 'Manuelt',
    },
    paintType: {
      [PaintType.Standard]: 'Standardlak',
      [PaintType.Metallic]: 'Metallak',
      [PaintType.Special]: 'Speciel',
      [PaintType.Pastel]: 'Pastellak',
      [PaintType.Unilack]: 'Unilak',
    },
    bodyTypes: {
      [BodyType.Cabriolet]: 'Cabriolet',
      [BodyType.Coupe]: 'Coupe',
      [BodyType.Hatchback]: 'Hatchback',
      [BodyType.MPV]: 'MPV',
      [BodyType.Pickup]: 'PickUp',
      [BodyType.Sedan]: 'Sedan',
      [BodyType.StationCar]: 'Stationcar',
      [BodyType.SUV]: 'SUV',
      [BodyType.Van]: 'Varebil',
    },
    submit: 'Sende',
    change: 'Skift',
    proceed: 'Fortsæt',
    close: 'Tilbage',
    moreInfo: 'Mere info',
    showMore: 'Vis flere',
    showAll: 'Vis alt',
    add: 'Vælg',
    remove: 'Fjern',
    noneSelected: 'Ingen valgt',
    selected: 'Valgt',
    standard: 'Standard',
    gear: 'Gear',
    fuelType: 'Brændstof',
    horsePower: 'Hestekræfter',
    consumption: 'Brændstofforbrug',
    drivingRange: 'Rækkevidde',
    cargoVolume: 'Varerumsvolumen',
    highlighted: 'Fremhævet',
    accessories: 'Tilbehør',
    vanEquipment: 'Opbygning',
    emission: (
      <>
        CO<sub>2</sub> udledning
      </>
    ),
    volume: 'Varerumsvolumen',
    batteryCapacity: 'Batteri',
    units: {
      consumption: 'km/l',
      emission: 'g/km',
      km: 'km',
      squareMeters: 'm3',
      millimeters: 'mm',
      year: 'år',
      percent: '%',
      kg: 'kg',
      hp: 'HK',
      batteryCapacity: 'kWh',
    },
    energyClasses: {
      [EnergyClass.A3Plus]: 'A+++',
      [EnergyClass.A2Plus]: 'A++',
      [EnergyClass.APlus]: 'A+',
      [EnergyClass.A]: 'A',
      [EnergyClass.B]: 'B',
      [EnergyClass.C]: 'C',
      [EnergyClass.D]: 'D',
      [EnergyClass.E]: 'E',
      [EnergyClass.F]: 'F',
      [EnergyClass.G]: 'G',
    },
    apply: 'Ansøge',
    reset: 'Nulstil',
    color: 'Farve',
    interior: 'Interiør',
    error: {
      serverDown: 'Serveren er nede',
      tryReload: 'Prøv at genindlæse eller vende tilbage senere',
    },
  },
  fields: {
    labels: {
      firstName: 'Fornavn',
      lastName: 'Efternavn',
      phoneNumber: 'Telefon',
      message: 'Besked til forhandler',
      email: 'E-mail',
      cvr: 'CVR',
      companyName: 'Firmanavn',
      zip: 'Postnr',
      city: 'By',
      address: 'Adresse',
      tradeIn: {
        isRequested: {
          [CommonAction.TestDriveBooking]: 'Vurder min brugte bil',
          [CommonAction.OfferRequest]: 'Min brugte bil skal indgå i tilbuddet',
        },
        plateNumber: 'Nummerplade',
        odometer: 'Antal kørte kilometer',
      },
      cpr: 'CPR',
    },
    errors: {
      required: 'Nødvendigt',
      phoneNumber: 'Forkert telefonnummer',
      email: 'Forkert email',
      cvr: 'Forkert cvr',
      zip: 'Forkert postnr',
      tradeIn: {
        plateNumber: 'Forkert nummerplade',
      },
      maxChars: (max: number): string => `Maks antal tegn ${max}`,
      cpr: 'Forkert cpr',
      min: (value: number): string => `Det skal være mere end eller lig med ${formatNumber(value)}`,
      max: (value: number): string => `Det skal være mindre end eller lig med ${formatNumber(value)}`,
    },
  },
  modules: {
    modelChooser: {
      title: 'Vælg din bil',
      wrongFilters: {
        title: 'Vi er kede af det',
        subTitle: 'Dine filterkriterier gav ikke et passende resultat',
        button: 'Vis alle modeller',
      },
      filters: {
        title: 'Filtre',
        fuelType: 'Brændstof',
        bodyType: 'Biltype',
        brand: 'Brand',
        price: 'Pris',
        min: 'min',
        max: 'max',
      },
      priceFrom: {
        [CarType.PersonalCar]: 'Vejl. udsalgspris fra',
        [CarType.Van]: 'Vejl. udsalgspris fra',
      },
      placeholder: {
        title: 'Beklager, men der er ingen tilgængelig model',
      },
    },
    modelViewer: {
      soldOut: {
        title: 'Udsolgt',
        modelSoldOut: 'Vi beklager, men denne model var udsolgt',
        tryAnotherModel: 'Prøv en anden model.',
        backToModels: 'Tilbage til modellisten',
      },
      notAvailable: {
        title: 'Mulighed ikke længere tilgængelig',
        text: 'Desværre er den promoverede bil ikke længere tilgængelig. Men du har mulighed for at konfigurere din egen bil.',
        button: 'Konfigurer',
      },
      overview: {
        back: 'Vælg bilmodel',
        disclaimer: {
          [CarType.PersonalCar]: 'Bilen kan være vist med ekstraudstyr. Der tages forbehold for pris - og afgiftsændringer samt tastefejl.',
          [CarType.Van]: 'Bilen kan være vist med ekstraudstyr. Der tages forbehold for pris - og afgiftsændringer samt tastefejl.',
        },
      },
      gallery: {
        video: {
          button: 'Se video',
          title: 'Videopræsentation',
        },
        thumbnails: {
          showMore: 'Vis mere',
          showLess: 'Vis mindre',
        },
      },
      configuration: {
        steps: {
          [CommonStep.Dimensions]: {
            title: 'Dimensioner',
            expandedTitle: 'Vælg dimensioner',
            volume: 'Varerumsvolumen',
            length: 'Længde',
          },
          [CommonStep.Trims]: {
            title: 'Udstyrsniveau',
            expandedTitle: 'Vælg udstyrsniveau',
            comparisonButtonTitle: 'Sammenlign',
            compareEquipment: {
              title: 'Sammenlign udstyrsniveau',
              backToConfigurator: 'Tilbage til konfigurator',
              equipment: {
                none: 'Ej muligt',
                optional: 'Ekstraudstyr',
                included: 'Inkluderet',
              },
            },
            trimsGroup: {
              availableSubTrimsLabel: 'ekstra udstyrspakke',
            },
          },
          [CommonStep.EnginesGears]: {
            title: 'Motorvariant',
            expandedTitle: 'Vælg motorvariant',
          },
          [CommonStep.Colors]: {
            expandedTitle: 'Vælg farve',
            roofColor: {
              title: 'Tagfarve',
            },
            colorPack: {
              title: 'Farvepakke',
            },
          },
          [CommonStep.Interiors]: {
            expandedTitle: 'Vælg interiør',
          },
          [CommonStep.Accessories]: {
            title: 'Tilbehør',
            expandedTitle: 'Vælg tilbehør',
            cartTitle: 'Valgt tilbehør',
          },
          [CommonStep.ExtraEquipment]: {
            title: 'Ekstraudstyr',
            expandedTitle: 'Vælg ekstraudstyr ',
            cartTitle: 'Vælg ekstraudstyr ',
            lockLabel: 'Begrænset',
            conflictModal: {
              title: 'Udstyrskonflikt',
              subtitle: 'Vælg venligst kun ét.',
              description: 'Dette udstyr er ikke kompatibelt med det allerede valgte.',
              continueButton: 'Fortsæt',
            },
          },
          [CommonStep.Weights]: {
            title: 'Vægt',
            expandedTitle: 'Vælg vægt',
            label: 'Totalvægt',
            description: 'Totalvægt = den maksimal tilladte køretøjsvægt med drivmidler, fører, passagerer og last.',
          },
          [CommonStep.VanEquipment]: {
            title: 'Opbygning',
            expandedTitle: 'Vælg opbygning',
            cartTitle: 'Tilføjet opbygning',
          },
          [CommonStep.ChargingSubscriptions]: {
            title: 'Ladeløsning',
            subscriptionPriceLabel: 'Abonnement',
            installationPriceLabel: 'Installation',
            expandedTitle: 'Vælg Ladeløsning',
            noCharge: 'Ingen ladeløsning',
            unitsPerMonth: 'kr. pr. md',
          },
        },
      },
      checkout: {
        title: {
          [CarType.PersonalCar]: 'Din bil',
          [CarType.Van]: 'Din bil',
        },
        total: 'Total',
        technicalSpecs: {
          emission: (
            <>
              CO<sub>2</sub> udledning
            </>
          ),
          ownerTax: 'Grøn ejerafgift',
        },
        color: 'Farve',
        interior: 'Interiør',
        standardEquipmentTitle: 'Standardudstyr',
        extraEquipmentTitle: 'Ekstraudstyr og tilbehør',
        van: {
          long: 'Længe',
        },
        actions: {
          consent: {
            text: (actionButton: string, privacyPolicyLink: string, clientPrivacyPolicyLink: string): string => (
              `Ved at klikke "${actionButton}" bekræfter jeg, at jeg er indforstået med behandlingen af mine persondata i henhold til følgende ${privacyPolicyLink}
              ${clientPrivacyPolicyLink ? `& den valgte forhandlernes ${clientPrivacyPolicyLink}` : ''}`
            ),
            linkText: 'persondatapolitik',
          },
          [CommonAction.TestDriveBooking]: {
            actionButtonTitle: 'Book prøvetur',
            title: 'Book prøvetur',
            successPage: {
              title: 'Tillykke',
              description: 'Du vil blive kontaktet hurtigst muligt',
            },
          },
          [CommonAction.OfferRequest]: {
            actionButtonTitle: 'Få et tilbud',
            title: 'Få et tilbud',
            successPage: {
              title: 'Tillykke',
              description: 'Du vil blive kontaktet hurtigst muligt',
            },
          },
          [CommonAction.ShareLink]: {
            actionButtonTitle: 'Gem bil',
            title: 'Indtast din e-mail',
            successPage: {
              description: 'Du vil inden for kort tid modtage en mail med et link til din bil',
            },
          },
        },
        mailingSubscriptionForm: {
          submit: 'Abonner og luk',
          consent: {
            text: (brand: string): string => `Jeg vil gerne modtage mails fra ${brand} Danmark med markedsføring af ${brand} produkter, information, tilbud og nyheder som relaterer sig til min kontakt. Læs mere om hvordan vi behandler dine oplysninger her`,
            linkText: 'persondatapolitik',
          },
        },
        storePicker: {
          title: 'Vælg forhandler',
          loadError: 'Desværre var der fejl ved indlæsning af forhandlerlisten. Prøv at opdatere eller gå videre, så kontakter vi dig.',
        },
        errorPage: {
          title: 'Fejl',
          description: 'Beklager, serveren svarer ikke',
          button: 'Opdater formularen',
        },
        hints: {
          [CommonAction.TestDriveBooking]: [
            'Udfyld formularen og send din forespørgsel om en prøvetur.',
            'Vores kunderådgiver vil modtage din henvendelse og sætte sig ind i dine ønsker, baseret på din valgte bil og evt. oplysninger om ønsket tidspunkt for prøveturen.',
            'Kunderådgiveren vil derefter kontakte dig med henblik på at booke en tid hos din valgte forhandler.',
          ],
          [CommonAction.OfferRequest]: [
            'Udfyld formularen og send din forespørgsel om et tilbud.',
            'Din valgte forhandler vil modtage din henvendelse og sætte sig ind i dine ønsker baseret på din valgte bil, og evt. oplysninger om brugt bil, hvis du ønsker at inkludere sådan en i tilbuddet.',
            'Forhandleren vil derefter kontakte dig for en uforpligtende samtale med et skræddersyet tilbud til dig.',
          ],
        },
      },
    },
  },
};

export default basicTexts;
